import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import * as api from '../lib/api';
import { toast } from 'sonner';
import { firstNameOf, formatApiError } from '../lib/format';
import { readInvite, forgetInvite } from '../lib/pendingInvite';

const SHOW_FORGOT_PASSWORD = true;

export const SignIn = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, signUp } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const from = location.state?.from || '/';
  const action = location.state?.action;
  const pending = readInvite();
  const inviteCode = location.state?.inviteCode || pending?.code;
  const inviterName = location.state?.inviterName || pending?.inviterName;

  // Arriving from an invite means creating an account by default, but an existing
  // member must still be able to sign in and keep the invite.
  const [isNewUser, setIsNewUser] = useState(
    Boolean(inviteCode) && location.state?.mode !== 'signin'
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim() || !email.includes('@')) {
      setError('Please enter a valid email');
      return;
    }
    if (!password || password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (isNewUser && !firstName.trim()) {
      setError('Please enter your first name');
      return;
    }

    setLoading(true);
    try {
      if (isNewUser) {
        const name = [firstName.trim(), lastName.trim()].filter(Boolean).join(' ');
        await signUp(email.trim(), password, name, inviteCode);
        forgetInvite();
        navigate('/connected', { replace: true, state: { inviterName } });
        return;
      }

      await signIn(email.trim(), password);

      if (inviteCode) {
        try {
          const result = await api.acceptInvite(inviteCode);
          forgetInvite();
          if (result?.status === 'already_connected') {
            toast.success(`You're already connected with ${firstNameOf(inviterName) || 'them'}`);
            navigate('/', { replace: true });
            return;
          }
          navigate('/connected', { replace: true, state: { inviterName } });
          return;
        } catch (inviteErr) {
          forgetInvite();
          if (inviteErr.response?.status === 409) {
            toast.success(`You're already connected with ${firstNameOf(inviterName) || 'them'}`);
            navigate('/', { replace: true });
            return;
          }
          toast.error('That invitation is no longer valid.');
        }
      }

      toast.success('Welcome back');
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page signin-page" data-testid="signin-page">
      <div className="signin-content">
        <div className="signin-header">
          <span className="welcome-brand">irecommend</span>
          <h1 className="signin-title">
            {isNewUser && inviterName ? `Join ${firstNameOf(inviterName)} on irecommend` : 'Welcome back'}
          </h1>
          <p className="signin-subtitle">
            {isNewUser
              ? 'Create your account to share recommendations with people you trust.'
              : action === 'save'
              ? 'Sign in to save recommendations for later.'
              : 'Sign in to see recommendations from people you trust.'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="signin-form">
          {isNewUser && (
            <>
              <div className="signin-field">
                <label className="signin-label" htmlFor="firstName">First name</label>
                <input
                  id="firstName"
                  type="text"
                  className="signin-input"
                  value={firstName}
                  maxLength={50}
                  onChange={(e) => { setFirstName(e.target.value); setError(''); }}
                  autoFocus
                  data-testid="signin-first-name"
                />
              </div>
              <div className="signin-field">
                <label className="signin-label" htmlFor="lastName">Last name</label>
                <input
                  id="lastName"
                  type="text"
                  className="signin-input"
                  value={lastName}
                  maxLength={50}
                  onChange={(e) => { setLastName(e.target.value); setError(''); }}
                  data-testid="signin-last-name"
                />
              </div>
            </>
          )}

          <div className="signin-field">
            <label className="signin-label" htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              className={`signin-input ${error && error.toLowerCase().includes('email') ? 'signin-input-error' : ''}`}
              placeholder="you@example.com"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(''); }}
              autoFocus={!isNewUser}
              data-testid="signin-email"
            />
          </div>

          <div className="signin-field">
            <label className="signin-label" htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              className={`signin-input ${error && error.toLowerCase().includes('password') ? 'signin-input-error' : ''}`}
              placeholder="At least 6 characters"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(''); }}
              data-testid="signin-password"
            />
          </div>

          {error && <span className="signin-error" data-testid="signin-error">{error}</span>}

          <button type="submit" className="signin-btn" disabled={loading} data-testid="signin-submit">
            {loading ? 'Please wait…' : isNewUser ? 'Join irecommend' : 'Sign in'}
          </button>
        </form>

        {SHOW_FORGOT_PASSWORD && !isNewUser && (
          <p className="signin-note">
            <Link className="signin-toggle" to="/forgot-password" data-testid="signin-forgot-link">
              Forgot your password?
            </Link>
          </p>
        )}

        <p className="signin-note">
          {inviteCode ? (
            isNewUser ? (
              <>
                Already have an account?{' '}
                <button
                  className="signin-toggle"
                  onClick={() => { setIsNewUser(false); setError(''); }}
                  data-testid="existing-user-toggle"
                >
                  Sign in
                </button>
              </>
            ) : (
              <>
                New here?{' '}
                <button
                  className="signin-toggle"
                  onClick={() => { setIsNewUser(true); setError(''); }}
                  data-testid="new-user-toggle"
                >
                  Create an account
                </button>
              </>
            )
          ) : (
            <span data-testid="invite-only-note">
              irecommend is invite only. You need an invitation from a member to join.
            </span>
          )}
        </p>
        <p className="legal-links" data-testid="signin-legal-links">
          <Link className="legal-links-item" to="/privacy" data-testid="signin-privacy-link">
            Privacy Statement
          </Link>
          <span className="legal-links-sep" aria-hidden="true">
            ·
          </span>
          <Link className="legal-links-item" to="/terms" data-testid="signin-terms-link">
            Terms of Use
          </Link>
        </p>
      </div>
    </div>
  );
};
